import { forwardRef, useId } from 'react'
import {
  FieldLabel,
  FIELD_CAPTION_TEXT_CLASS,
  type FieldCaptionSize,
} from '@/components/FieldLabel'
import { cn } from '@/lib/utils'

type TextFieldProps = {
  label?: string
  placeholder?: string
  disabled?: boolean
  type?:
    | 'text'
    | 'number'
    | 'password'
    | 'email'
    | 'search'
    | 'tel'
    | 'url'
    | 'date'
    | 'color'
  inverse?: boolean
  size?: FieldCaptionSize
  required?: boolean
  error?: string
  fieldNote?: string
  id?: string
  className?: string
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'type'>

const sizeConfig = {
  sm: {
    wrapper: 'gap-[6px]',
    input: 'px-sm py-[6px] text-[14px] leading-[20px] tracking-[-0.21px]',
  },
  md: {
    wrapper: 'gap-[6px]',
    input: 'px-sm py-xs text-[16px] leading-[24px] tracking-[-0.24px]',
  },
} as const

const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  (
    {
      label,
      placeholder,
      disabled = false,
      type = 'text',
      inverse = false,
      size = 'sm',
      required,
      error,
      fieldNote,
      id,
      className,
      ...rest
    },
    ref
  ) => {
    const generatedId = useId()
    const fieldId = id ?? generatedId
    const errorId = error ? `${fieldId}-error` : undefined
    const hintId = fieldNote && !error ? `${fieldId}-hint` : undefined
    const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined
    const sizes = sizeConfig[size]

    return (
      <div className={cn('flex w-full flex-col', sizes.wrapper)}>
        <div className='flex w-full flex-col gap-0'>
          {label ? (
            <FieldLabel
              htmlFor={fieldId}
              label={label}
              required={required}
              inverse={inverse}
              disabled={disabled}
              captionTextClassName={FIELD_CAPTION_TEXT_CLASS[size]}
            />
          ) : null}
          <input
            ref={ref}
            id={fieldId}
            placeholder={placeholder}
            disabled={disabled}
            type={type}
            required={required}
            aria-required={required ? 'true' : undefined}
            aria-invalid={!!error}
            aria-describedby={describedBy}
            className={cn(
              'w-full rounded-lg border text-left font-normal [font-family:inherit] transition-shadow duration-sm',
              sizes.input,
              inverse
                ? [
                    'border-white/25 bg-transparent text-white placeholder:text-white/50',
                    'hover:border-white/40',
                    'focus:border-white focus:outline-none',
                    disabled && 'cursor-not-allowed opacity-50',
                  ]
                : [
                    'bg-white',
                    disabled
                      ? 'cursor-not-allowed border-input-border-disabled text-text-disabled placeholder:text-text-disabled'
                      : error
                        ? 'border-input-border-error text-secondary shadow-input placeholder:text-text-placeholder focus:shadow-input-error-focus focus:outline-none'
                        : 'border-input-border text-secondary shadow-input placeholder:text-text-placeholder focus:shadow-input-focus focus:outline-none',
                  ],
              className
            )}
            {...rest}
          />
        </div>
        {error && (
          <span
            id={errorId}
            className={cn(
              'text-[14px] leading-[20px] tracking-[-0.21px]',
              inverse ? 'text-error' : 'text-input-border-error'
            )}
            role='alert'
          >
            {error}
          </span>
        )}
        {fieldNote && !error && (
          <span
            id={hintId}
            className={cn(
              'text-[14px] leading-[20px] tracking-[-0.21px]',
              inverse
                ? 'text-white/70'
                : disabled
                  ? 'text-text-disabled'
                  : 'text-secondary'
            )}
          >
            {fieldNote}
          </span>
        )}
      </div>
    )
  }
)

TextField.displayName = 'TextField'

export { TextField }
export type { TextFieldProps }
